import noop from "lodash/noop";
import { useCallback, useReducer } from "react";
import { ModalConfirmationProps } from "../types/ModalConfirmationProps";

type ConfirmationAction =
  | {
      type: "OPEN_MODAL";
      payload: { resolve: (isConfirmed: boolean) => void };
    }
  | { type: "CLOSE_MODAL" };

const reducer = (
  prevState: ModalConfirmationProps,
  action: ConfirmationAction
) => {
  switch (action.type) {
    case "OPEN_MODAL":
      return {
        show: true,
        onConfirm: () => action.payload.resolve(true),
        onClose: () => action.payload.resolve(false),
      };
    case "CLOSE_MODAL":
      return {
        show: false,
        onConfirm: noop,
        onClose: noop,
      };
    default:
      return prevState;
  }
};

export const useModalConfirmationState = (): ModalConfirmationProps & {
  waitForConfirmation: () => Promise<boolean>;
} => {
  const [state, dispatch] = useReducer(reducer, {
    show: false,
    onConfirm: noop,
    onClose: noop,
  });
  const waitForConfirmation = useCallback(
    (): Promise<boolean> =>
      new Promise(resolve => {
        dispatch({ type: "OPEN_MODAL", payload: { resolve } });
      }),
    [dispatch]
  );
  const onConfirm = useCallback(() => {
    state.onConfirm();
    dispatch({ type: "CLOSE_MODAL" });
  }, [state, dispatch]);

  const onClose = useCallback(() => {
    state.onClose();
    dispatch({ type: "CLOSE_MODAL" });
  }, [state, dispatch]);

  return {
    show: state.show,
    /**
     * opens the confirmation modal and returns a promise to its result once it closes.
     */
    waitForConfirmation,
    onConfirm,
    onClose,
  };
};
